import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Chip,
  Button,
  Checkbox,
} from '@material-tailwind/react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline';

const REASON_LABELS = {
  missing_price: 'Missing price',
  outside_year_range: 'Outside year range',
  missing_year: 'Missing year',
  missing_total_time: 'Missing hours',
  outside_date_range: 'Outside date range',
  duplicate: 'Duplicate',
  invalid_data: 'Invalid data',
  user_excluded: 'User excluded',
};

const formatPrice = (value) => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  try {
    const date = new Date(value);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

const isLive = (lastSeenDate) => {
  if (!lastSeenDate) return false;
  const lastSeen = new Date(lastSeenDate);
  const fourDaysAgo = new Date();
  fourDaysAgo.setDate(fourDaysAgo.getDate() - 4);
  return lastSeen >= fourDaysAgo;
};

function ListingsSummaryPanel({
  listingsSummary,
  periodDays,
  onRevertExclusion,
  onRevertAllExclusions,
  effectiveExcludedListings = [],
  draftExcludedListingRegs = [],
  hasExclusionChanges = false,
  includedNoPriceListings = [],
  onToggleNoPriceListing,
}) {
  // Derive pending states by comparing effective vs draft (case-insensitive)
  const effectiveUpper = effectiveExcludedListings.map(r => r.toUpperCase());
  const draftUpper = draftExcludedListingRegs.map(r => r.toUpperCase());
  const pendingExclusions = effectiveExcludedListings.filter(r => !draftUpper.includes(r.toUpperCase()));
  const pendingReversions = draftExcludedListingRegs.filter(r => !effectiveUpper.includes(r.toUpperCase()));
  const panelTitle = periodDays ? `Listings (${periodDays} days)` : 'Listings';
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortListings = (listings) => {
    if (!sortConfig.key || !listings) return listings;

    return [...listings].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.key) {
        case 'reg':
          aVal = a.reg || a.registration || '';
          bVal = b.reg || b.registration || '';
          break;
        case 'year':
          aVal = a.year || a.model_year || 0;
          bVal = b.year || b.model_year || 0;
          break;
        case 'hours':
          aVal = a.totalTime || a.total_time || a.hours || 0;
          bVal = b.totalTime || b.total_time || b.hours || 0;
          break;
        case 'price':
          aVal = a.price_usd || a.price || 0;
          bVal = b.price_usd || b.price || 0;
          break;
        case 'priceChange':
          aVal = a.priceChangePct ?? 0;
          bVal = b.priceChangePct ?? 0;
          break;
        case 'source':
          aVal = a.source || '';
          bVal = b.source || '';
          break;
        case 'firstSeen':
          aVal = new Date(a.firstSeen || a.first_seen || 0).getTime();
          bVal = new Date(b.firstSeen || b.first_seen || 0).getTime();
          break;
        case 'lastSeen':
          aVal = new Date(a.lastSeen || a.last_seen || 0).getTime();
          bVal = new Date(b.lastSeen || b.last_seen || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortHeader = ({ label, sortKey }) => (
    <th
      className="p-2 cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey && (
          <span className="text-blue-500">
            {sortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  if (!listingsSummary) return null;

  const {
    totalFetched,
    includedListings,
    excludedListings,
  } = listingsSummary;

  // Derive counts from array lengths (totalIncluded/totalExcluded removed from backend)
  const totalIncluded = includedListings?.length ?? 0;
  const totalExcluded = excludedListings?.length ?? 0;

  const inclusionRate = totalFetched > 0
    ? Math.round((totalIncluded / totalFetched) * 100)
    : 0;

  // Check if there are user-excluded listings (only those with a price are revertable)
  const userExcludedCount = excludedListings
    ? excludedListings.filter(l => l.reason === 'user_excluded' && (l.price_usd || l.price)).length
    : 0;

  // Calculate average price from included listings
  const avgPrice = (() => {
    if (!includedListings || includedListings.length === 0) return 0;
    const prices = includedListings
      .map(l => l.price_usd || l.price || l.priceUsd)
      .filter(p => p && p > 0);
    return prices.length > 0
      ? prices.reduce((a, b) => a + b, 0) / prices.length
      : 0;
  })();

  // Calculate how many included listings are live
  const liveCount = includedListings
    ? includedListings.filter(l => isLive(l.lastSeen || l.last_seen)).length
    : 0;

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <QueueListIcon className="h-5 w-5 text-blue-500" />
            <Typography variant="h6" color="blue-gray">
              {panelTitle}
            </Typography>
          </div>
          {userExcludedCount > 0 && onRevertAllExclusions && (
            <Button
              variant="outlined"
              size="sm"
              color="amber"
              className="py-1 px-2 text-xs flex items-center gap-1"
              onClick={onRevertAllExclusions}
              disabled={pendingReversions.length === userExcludedCount}
            >
              {pendingReversions.length === userExcludedCount ? 'All Pending' : `Revert All (${userExcludedCount})`}
            </Button>
          )}
        </div>

        {/* Summary Stats */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Chip size="sm" value={`${totalFetched} found`} variant="ghost" color="blue-gray" />
          <Chip size="sm" value={`${totalIncluded} included`} variant="ghost" color="green" />
          {liveCount > 0 && (
            <Chip size="sm" value={`${liveCount} live`} variant="ghost" color="blue" />
          )}
          {totalExcluded > 0 && (
            <Chip size="sm" value={`${totalExcluded} excluded`} variant="ghost" color="amber" />
          )}
          <div className="flex items-center gap-1 ml-2">
            {inclusionRate >= 50 ? (
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
            ) : (
              <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
            )}
            <Typography variant="small" color="gray">
              {inclusionRate}% included
            </Typography>
          </div>
        </div>

        {/* Included Listings */}
        {includedListings && includedListings.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Typography variant="small" className="font-semibold text-green-700">
                  Included Listings ({includedListings.length})
                </Typography>
                {pendingExclusions.length > 0 && (
                  <Chip
                    size="sm"
                    value={`${pendingExclusions.length} pending exclude`}
                    color="amber"
                    variant="ghost"
                  />
                )}
              </div>
              <Typography variant="small" className="font-medium text-green-700">
                Avg: {formatPrice(avgPrice)}
              </Typography>
            </div>
            <div className="max-h-64 overflow-auto border rounded">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-gray-50 sticky top-0">
                    <SortHeader label="Reg" sortKey="reg" />
                    <SortHeader label="Year" sortKey="year" />
                    <SortHeader label="Hours" sortKey="hours" />
                    <SortHeader label="Price" sortKey="price" />
                    <SortHeader label="Chg" sortKey="priceChange" />
                    <SortHeader label="Source" sortKey="source" />
                    <SortHeader label="First Seen" sortKey="firstSeen" />
                    <SortHeader label="Last Seen" sortKey="lastSeen" />
                    <th className="p-2">Live</th>
                    {pendingExclusions.length > 0 && <th className="p-2">Status</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortListings(includedListings).map((listing, idx) => {
                    const reg = listing.reg || listing.registration;
                    const lastSeenDate = listing.lastSeen || listing.last_seen;
                    const listingIsLive = isLive(lastSeenDate);
                    const isPendingExclude = pendingExclusions.some(r => r.toUpperCase() === reg?.toUpperCase());
                    return (
                      <tr key={listing.id || idx} className={`border-b hover:bg-gray-50 ${isPendingExclude ? 'bg-amber-50' : ''}`}>
                        <td className="p-2">
                          <Typography variant="small" className={`font-medium ${isPendingExclude ? 'line-through text-gray-400' : ''}`}>
                            {reg || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingExclude ? 'text-gray-400' : ''}>
                            {listing.year || listing.model_year || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingExclude ? 'text-gray-400' : ''}>
                            {(listing.totalTime || listing.total_time || listing.hours || listing.totalHours || listing.ttaf || listing.airframeHours)?.toLocaleString() || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={`font-medium ${isPendingExclude ? 'text-gray-400' : 'text-green-700'}`}>
                            {formatPrice(listing.price_usd || listing.price)}
                          </Typography>
                        </td>
                        <td className="p-2">
                          {listing.priceChangePct != null && listing.priceChangePct !== 0 && (
                            <Typography variant="small" className={`font-medium ${listing.priceChangePct < 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {listing.priceChangePct < 0 ? '↓' : '↑'} {Math.abs(listing.priceChangePct * 100).toFixed(1)}%
                            </Typography>
                          )}
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingExclude ? 'text-gray-400' : ''}>
                            {listing.source || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingExclude ? 'text-gray-400' : ''}>
                            {formatDate(listing.firstSeen || listing.first_seen)}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingExclude ? 'text-gray-400' : ''}>
                            {formatDate(lastSeenDate)}
                          </Typography>
                        </td>
                        <td className="p-2 text-center">
                          {listingIsLive ? (
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full" title="Live (seen in last 4 days)" />
                          ) : (
                            <span className="inline-block w-2 h-2 bg-gray-300 rounded-full" title="Not live" />
                          )}
                        </td>
                        {pendingExclusions.length > 0 && (
                          <td className="p-2">
                            {isPendingExclude && (
                              <Chip
                                size="sm"
                                value="Pending"
                                variant="ghost"
                                color="amber"
                              />
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No-Price Listings (selectable for display) */}
        {excludedListings && excludedListings.filter(l => l.reason === 'missing_price').length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Typography variant="small" className="font-semibold text-gray-700">
                No-Price Listings ({excludedListings.filter(l => l.reason === 'missing_price').length})
              </Typography>
              <Typography variant="small" className="text-gray-500">
                Select to show in report
              </Typography>
              {includedNoPriceListings.length > 0 && (
                <Chip
                  size="sm"
                  value={`${includedNoPriceListings.length} selected`}
                  color="blue"
                  variant="ghost"
                />
              )}
            </div>
            <div className="max-h-48 overflow-auto border rounded border-dashed border-gray-300">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-gray-50 sticky top-0">
                    <th className="p-2 w-10"></th>
                    <th className="p-2">Reg</th>
                    <th className="p-2">Year</th>
                    <th className="p-2">Hours</th>
                    <th className="p-2">Source</th>
                    <th className="p-2">Live</th>
                  </tr>
                </thead>
                <tbody>
                  {excludedListings.filter(l => l.reason === 'missing_price').map((listing, idx) => {
                    const reg = listing.reg || listing.registration;
                    const listingId = listing.id || listing.uniqueListingsId;
                    const lastSeenDate = listing.lastSeen || listing.last_seen;
                    const listingIsLive = isLive(lastSeenDate);
                    const isSelected = includedNoPriceListings.includes(listingId);
                    return (
                      <tr
                        key={listingId || idx}
                        className={`border-b hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                        onClick={() => onToggleNoPriceListing && onToggleNoPriceListing(listingId)}
                      >
                        <td className="p-2">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => {}}
                            containerProps={{ className: 'p-0' }}
                            className="h-4 w-4"
                          />
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className="font-medium">
                            {reg || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small">
                            {listing.year || listing.model_year || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small">
                            {(listing.totalTime || listing.total_time || listing.hours)?.toLocaleString() || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small">
                            {listing.source || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2 text-center">
                          {listingIsLive ? (
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full" title="Live (seen in last 4 days)" />
                          ) : (
                            <span className="inline-block w-2 h-2 bg-gray-300 rounded-full" title="Not live" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Other Excluded Listings */}
        {excludedListings && excludedListings.filter(l => l.reason !== 'missing_price').length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Typography variant="small" className="font-semibold text-amber-700">
                Excluded Listings ({excludedListings.filter(l => l.reason !== 'missing_price').length})
              </Typography>
              {pendingReversions.length > 0 && (
                <Chip
                  size="sm"
                  value={`${pendingReversions.length} pending restore`}
                  color="blue"
                  variant="ghost"
                />
              )}
            </div>
            <div className="max-h-48 overflow-auto border rounded">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-gray-50 sticky top-0">
                    <th className="p-2">Reg</th>
                    <th className="p-2">Year</th>
                    <th className="p-2">Hours</th>
                    <th className="p-2">Price</th>
                    <th className="p-2">Chg</th>
                    <th className="p-2">Source</th>
                    <th className="p-2">Live</th>
                    <th className="p-2">Reason</th>
                    <th className="p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {excludedListings.filter(l => l.reason !== 'missing_price').map((listing, idx) => {
                    const isUserExcluded = listing.reason === 'user_excluded';
                    const reg = listing.reg || listing.registration;
                    const lastSeenDate = listing.lastSeen || listing.last_seen;
                    const listingIsLive = isLive(lastSeenDate);
                    const isPendingRestore = pendingReversions.some(r => r.toUpperCase() === reg?.toUpperCase());
                    return (
                      <tr key={listing.id || idx} className={`border-b ${isPendingRestore ? 'bg-blue-50' : isUserExcluded ? 'bg-amber-50' : ''}`}>
                        <td className="p-2">
                          <Typography variant="small" className={`font-medium ${isPendingRestore ? 'line-through text-gray-400' : ''}`}>
                            {reg || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingRestore ? 'text-gray-400' : ''}>
                            {listing.year || listing.model_year || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingRestore ? 'text-gray-400' : ''}>
                            {(listing.totalTime || listing.total_time || listing.hours)?.toLocaleString() || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingRestore ? 'text-gray-400' : ''}>
                            {formatPrice(listing.price_usd || listing.price)}
                          </Typography>
                        </td>
                        <td className="p-2">
                          {listing.priceChangePct != null && listing.priceChangePct !== 0 && (
                            <Typography variant="small" className={`font-medium ${listing.priceChangePct < 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {listing.priceChangePct < 0 ? '↓' : '↑'} {Math.abs(listing.priceChangePct * 100).toFixed(1)}%
                            </Typography>
                          )}
                        </td>
                        <td className="p-2">
                          <Typography variant="small" className={isPendingRestore ? 'text-gray-400' : ''}>
                            {listing.source || 'N/A'}
                          </Typography>
                        </td>
                        <td className="p-2 text-center">
                          {listingIsLive ? (
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full" title="Live (seen in last 4 days)" />
                          ) : (
                            <span className="inline-block w-2 h-2 bg-gray-300 rounded-full" title="Not live" />
                          )}
                        </td>
                        <td className="p-2">
                          {isPendingRestore ? (
                            <Chip
                              size="sm"
                              value="Pending"
                              variant="ghost"
                              color="blue"
                            />
                          ) : (
                            <Chip
                              size="sm"
                              value={REASON_LABELS[listing.reason] || listing.reason}
                              variant="ghost"
                              color={isUserExcluded ? 'amber' : 'gray'}
                            />
                          )}
                        </td>
                        <td className="p-2">
                          {isUserExcluded && (listing.price_usd || listing.price) && onRevertExclusion && !isPendingRestore && (
                            <Button
                              variant="text"
                              size="sm"
                              color="amber"
                              className="py-1 px-2 text-xs"
                              onClick={() => onRevertExclusion(reg)}
                            >
                              Revert
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </CardBody>
    </Card>
  );
}

export default ListingsSummaryPanel;
